<template>
  <admin-page-shell :title="$t('frontend_groovemail_admin_import_user_approvals')">
    <template slot="tabs">
      <gd-tabs
        :tabs="tabs"
        :currentTab="currentTab"
        @onClick="handleTabChange"
      />
    </template>

    <div class="gm-admin-tab groovemail-import-users-table">
      <div class="gm-admin-tab__head">
        <div>
          <h2>{{ import_tab_header }}</h2>
        </div>
        <div
          class="gm-import-view-switch"
          v-if="currentTab !== 'processingFiles'"
        >
          <gd-dropdown
            class="text-left"
            size="auto"
            v-model="percentSwitch"
            :options="dropdownOptions"
            @change="changeDataView"
          />
        </div>
      </div>
      <div v-if="currentTab === 'processingFiles'" class="gm-admin-tab__listing">
        <processing-leads
          ref="processingImportsListing"
          importSourceUrl="/admin/user-leads-listing"
          :importLeadType="4"
          @downloadCsvOfLeads="downloadCsvOfLeads"
        ></processing-leads>
      </div>
      <div v-if="currentTab === 'pendingFiles'" class="gm-admin-tab__listing">
        <pending-leads
          ref="pendingImportsListing"
          importSourceUrl="/admin/user-leads-listing"
          :importLeadType="0"
          :percentSwitch="percentSwitch"
          @downloadCsvOfLeads="downloadCsvOfLeads"
          @rejectLeadMethod="rejectLeadMethod"
          @approveAllLeadsInCsv="openConfirmationActionModal"
          @approveGoodLeadsInCsv="openConfirmationActionModal"
          @showResponsesForThisCsv="showResponsesForThisCsv"
          @showMappingsForThisCsv="showMappingsForThisCsv"
          @showGatewaysForThisCsv="showGatewaysForThisCsv"
          @showImportsForThisUser="showImportsForThisUser"
        ></pending-leads>
      </div>
      <div v-if="currentTab === 'approvedFiles'" class="gm-admin-tab__listing">
        <approved-leads
          ref="approvedImportsListing"
          importSourceUrl="/admin/user-leads-listing"
          :importLeadType="1"
          :percentSwitch="percentSwitch"
          @rejectLeadMethod="rejectLeadMethod"
          @downloadCsvOfLeads="downloadCsvOfLeads"
          @showResponsesForThisCsv="showResponsesForThisCsv"
          @showMappingsForThisCsv="showMappingsForThisCsv"
        ></approved-leads>
      </div>
      <div v-if="currentTab === 'rejectedFiles'" class="gm-admin-tab__listing">
        <rejected-leads
          ref="rejectedImportsListing"
          importSourceUrl="/admin/user-leads-listing"
          :importLeadType="3"
          :percentSwitch="percentSwitch"
          @downloadCsvOfLeads="downloadCsvOfLeads"
          @approveAllLeadsInCsv="openConfirmationActionModal"
          @approveGoodLeadsInCsv="openConfirmationActionModal"
          @showResponsesForThisCsv="showResponsesForThisCsv"
          @showMappingsForThisCsv="showMappingsForThisCsv"
        ></rejected-leads>
      </div>
    </div>

    <gs-named-modal
      id="import-responses-modal"
      name="import-responses-modal"
      :title="
        this.$t('frontend_groovemail_admin_import_user_approval_csv_response')
      "
    >
      <div class="gm-admin-modal__body">
        <ImportResponses
          :importResponsesData="importResponsesData"
        ></ImportResponses>
      </div>
    </gs-named-modal>

    <gs-named-modal
      id="import-mappings-modal"
      name="import-mappings-modal"
      :title="
        this.$t(
          'frontend_groovemail_admin_import_user_approval_csv_field_mappins'
        )
      "
    >
      <div class="gm-admin-modal__body">
        <ImportMappingAndAnalysis
          :importData="importData"
        ></ImportMappingAndAnalysis>
      </div>
    </gs-named-modal>

    <gs-named-modal
      id="reject-leads-csv-modal"
      name="reject-leads-csv-modal"
      :title="
        this.$t(
          'frontend_groovemail_admin_import_user_approval_csv_file_of_leads_reject'
        )
      "
    >
      <div class="gm-admin-modal__body">
        <gs-field
          :label="$t('frontend_groovemail_admin_import_user_approval_csv_file_of_leads_reject_enter_reason')"
        >
          <text-area
            variant="base"
            v-model="rejectionModel.rejectionReason"
            :placeholder="$t('frontend_groovemail_suppressions_enter_emails_unsubscribers_enter_reason')"
            rows="3"
          />
          <p
            v-if="
              rejectionModel.rejectionSubmitted &&
              rejectionModel.rejectWithEmail === 1 &&
              (!rejectionModel.rejectionReason.trim() ||
                !rejectionModel.rejectionReason.trim())
            "
            class="gm-admin-tab__error"
          >
            {{
              this.$t(
                'frontend_groovemail_admin_import_user_approval_leads_reject_reason_data'
              )
            }}
          </p>
        </gs-field>
        <div class="gm-admin-modal__footer">
          <gs-button variant="outline" @click="cancelLeadsRejection()">{{ $t('frontend_cancel') }}</gs-button>
          <gs-button variant="outline" @click="rejectAllLeads(0)">
            {{
              $t(
                'frontend_groovemail_admin_import_user_approval_csv_file_of_leads_reject_without_mail'
              )
            }}
          </gs-button>
          <gs-button variant="primary" @click="rejectAllLeads(1)">
            {{
              $t(
                'frontend_groovemail_admin_import_user_approval_csv_file_of_leads_reject_with_mail'
              )
            }}
          </gs-button>
        </div>
      </div>
    </gs-named-modal>

    <gs-named-modal
      id="senders-gateways-modal"
      name="senders-gateways-modal"
      :title="
        this.$t('frontend_groovemail_import_admin_sender_and_gateway_info')
      "
    >
      <div class="gm-admin-modal__body">
        <SendersDetail :sendersGatewayData="sendersGatewayData"></SendersDetail>
      </div>
    </gs-named-modal>

    <!-- Confirmation delete modal -->
    <confirm-modal
      :ref="`confirmation-action`"
      :name="`confirmation-action`"
      :buttonText="$t('frontend_confirm')"
      :cancelText="$t('frontend_cancel')"
      :confirmMessage="this.$t('frontend_bouses_delete_bonus_title')"
      :messageContent="confirmMessage"
      @confirm="confirmAction"
      @close="closeConfirmationActionModal"
    />
  </admin-page-shell>
</template>

<script>
import VuePerfectScrollbar from 'vue-perfect-scrollbar';
import ProcessingLeads from './processing-leads.vue';
import PendingLeads from './pending-leads.vue';
import ApprovedLeads from './approved-leads.vue';
import RejectedLeads from './rejected-leads.vue';
import ImportResponses from './import-responses.vue';
import ImportMappingAndAnalysis from './import-mapping-and-analysis.vue';
import SendersDetail from './senders-detail.vue';
import AdminPageShell from '../AdminPageShell.vue';
import { adminUrls } from '@/utils/urls.js';
import { postData } from '@/utils/apiHelper.js';
import { showRequestErrorAlert } from '@/components/showDialog.js';
import downloadCsvFile from '@/components/downloadCsvFile';

export default {
  middleware: 'auth',

  metaInfo() {
    return {
      title: this.$t('frontend_groovemail_admin_import_user_approvals'),
    };
  },

  data() {
    return {
      currentTab: 'pendingFiles',
      percentSwitch: false,
      rejectionModel: {
        rejectionFileId: -1,
        rejectWithEmail: 0,
        rejectionReason: '',
        rejectionSubmitted: false,
        importStatus: '',
      },
      importData: [],
      importResponsesData: [],
      sendersGatewayData: [],
      dropdownOptions: [
        {
          text: this.$t('frontend_groovemail_campaign_listing_show_data_as_percentage'),
          showValue: `${this.$t('frontend_groovemail_campaign_listing_show_data_as')} (%)`,
          value: true,
        },
        {
          text: this.$t('frontend_groovemail_campaign_listing_show_data_as_number'),
          showValue: `${this.$t('frontend_groovemail_campaign_listing_show_data_as')} (#)`,
          value: false,
        }
      ],

      confirmMessage: '',
      confirmCsvData: '',
      confirmImportType: '',
    };
  },

  components: {
    AdminPageShell,
    VuePerfectScrollbar,
    ProcessingLeads,
    PendingLeads,
    ApprovedLeads,
    RejectedLeads,
	  ImportResponses,
    ImportMappingAndAnalysis,
    SendersDetail,
  },

  computed: {
	  /**
     * Returns computed title for show data as dropdown based on what format user wants data in.
     * @return {string} title for show data as dropdown
     */
    showDataAsTitle() {
      if (this.percentSwitch) {
        return `${this.$t(
          'frontend_groovemail_campaign_listing_show_data_as'
        )} (%)`;
      }
      return `${this.$t(
        'frontend_groovemail_campaign_listing_show_data_as'
      )} (#)`;
    },

	  /**
     * Returns import tab header based on current tab.
     * @return {string} tab header
     */
    import_tab_header() {
      let tabHeader = '';
      if (this.currentTab === 'processingFiles') {
        tabHeader = this.$t(
          'frontend_groovemail_import_user_listing_processing_status'
        );
      } else if (this.currentTab === 'pendingFiles') {
        tabHeader = this.$t(
          'frontend_groovemail_import_user_listing_pending_status'
        );
      } else if (this.currentTab === 'approvedFiles') {
        tabHeader = this.$t(
          'frontend_groovemail_import_user_listing_approved_status'
        );
      } else if (this.currentTab === 'rejectedFiles') {
        tabHeader = this.$t(
          'frontend_groovemail_import_user_listing_rejected_status'
        );
      }

      return tabHeader;
    },

	  /**
     * Returns tabs data for imports.
     * @return {array} tab data
     */
    tabs() {
      return [
        {
          title: `<span class="text-sm font-semibold px-4">${this.$t(
            'frontend_groovemail_import_user_listing_processing_status'
          )}</span>`,
          value: 'processingFiles',
        },
        {
          title: `<span class="text-sm font-semibold px-4">${this.$t(
            'frontend_groovemail_import_user_listing_pending_status'
          )}</span>`,
          value: 'pendingFiles',
        },
        {
          title: `<span class="text-sm font-semibold px-4">${this.$t(
            'frontend_groovemail_import_user_listing_approved_status'
          )}</span>`,
          value: 'approvedFiles',
        },
        {
          title: `<span class="text-sm font-semibold px-4">${this.$t(
            'frontend_groovemail_import_user_listing_rejected_status'
          )}</span>`,
          value: 'rejectedFiles',
        },
      ];
    },

	  /**
     * Returns main tab class for tabs.
     * @return {string} tab class
     */
    mainTabClass() {
      return 'pb-4 gs-default-tabs-item';
    },
  },

  mounted() {
    // sets current tab based on tab query
    if (this.$route.query.type) {
      this.currentTab = this.$route.query.type;
    }
  },

  methods: {
    /**
     * Used to show data in percentage format.
     * @param {boolean} value (true/false)
     */
    changeDataView(value) {
      this.percentSwitch = value;
    },

	  /**
     * Triggers downloading of csv of different type of contacts (all, good, bad).
     * @param {object} csvData data related to csv file
     * @param {string} url url for downloading data
     * @param {number} type type of contact
     *    0 - bad contacts
     *    1 - good contacts
     *    2 - all contacts
     */
    async downloadCsvOfLeads(csvData, url, type) {
	    let fileName = '';
      if (type === 1) {
        fileName = `good_${csvData.csv_name}`;
      } else if (type === 2) {
        fileName = `all_${csvData.csv_name}`;
      } else if (type === 0) {
        fileName = `bad_${csvData.csv_name}`;
      }

      try {
        await downloadCsvFile(`${url}/${csvData.id}`, null, fileName);
      } catch (error) {
        // show error alert
        await showRequestErrorAlert(error?.response?.data?.error);
      }
    },

    /**
     * Triggers opening of contacts import rejection modal.
     * @param {object} csvData data related to csv file
     * @param {object} importStatus status
     */
    async rejectLeadMethod(csvData, importStatus) {
      this.rejectionModel.rejectionFileId = csvData.id;
      this.rejectionModel.importStatus = importStatus;
      this.$root.$emit('modal-open', 'reject-leads-csv-modal');
    },

    // Triggers closing of contacts import rejection modal.
    cancelLeadsRejection() {
      this.$root.$emit('modal-close', 'reject-leads-csv-modal');
    },
	
    /**
     * Triggers rejecting all contacts
     * @param {number} type type of rejection
     *    0 - rejection of contact imports without email
     *    1 - rejection of contact imports with email
     */
    async rejectAllLeads(type) {
      this.rejectionModel.rejectionSubmitted = true;
      this.rejectionModel.rejectWithEmail = type;
      if (
        (this.rejectionModel.rejectWithEmail &&
          this.rejectionModel.rejectionReason) ||
        this.rejectionModel.rejectWithEmail === 0
      ) {
        const rejectionPayload = {
          email_send: this.rejectionModel.rejectWithEmail,
          id: this.rejectionModel.rejectionFileId,
          reject_reason: this.rejectionModel.rejectionReason,
          import_status: this.rejectionModel.importStatus,
        };
	      const rejectionResponse = await postData(
        adminUrls.importManagementUrl.rejectAllLeads,
        rejectionPayload);
        if (
          rejectionResponse &&
          rejectionResponse.data &&
          rejectionResponse.data.status === 200
        ) {
          if (
            this.$refs.pendingImportsListing &&
            this.$refs.pendingImportsListing.$refs &&
            this.$refs.pendingImportsListing.$refs.importListing &&
            this.$refs.pendingImportsListing.$refs.importListing.$refs &&
            this.$refs.pendingImportsListing.$refs.importListing.$refs
              .serverTable &&
            this.$refs.pendingImportsListing.$refs.importListing.$refs
              .serverTable.$refs.importTable
          ) {
            const pageCount = this.getPageCount(
              this.$refs.pendingImportsListing.$refs.importListing.$refs
                .serverTable.$refs.importTable.count,
              this.$refs.pendingImportsListing.importedUsersTableOptions
                .perPage,
              this.$refs.pendingImportsListing.$refs.importListing.$refs
                .serverTable.$refs.importTable.Page
            );
            await this.$refs.pendingImportsListing.$refs.importListing.$refs.serverTable.$refs.importTable.setPage(
              pageCount
            );
          }else if(
            this.$refs.approvedImportsListing &&
            this.$refs.approvedImportsListing.$refs &&
            this.$refs.approvedImportsListing.$refs.importListing &&
            this.$refs.approvedImportsListing.$refs.importListing.$refs &&
            this.$refs.approvedImportsListing.$refs.importListing.$refs
              .serverTable &&
            this.$refs.approvedImportsListing.$refs.importListing.$refs
              .serverTable.$refs.importTable){
                const pageCount = this.getPageCount(
                  this.$refs.approvedImportsListing.$refs.importListing.$refs
                  .serverTable.$refs.importTable.count,
                  this.$refs.approvedImportsListing.importedUsersTableOptions
                  .perPage,
                  this.$refs.approvedImportsListing.$refs.importListing.$refs
                  .serverTable.$refs.importTable.Page
            );
            await this.$refs.approvedImportsListing.$refs.importListing.$refs.serverTable.$refs.importTable.setPage(
              pageCount
            );
          }
          this.rejectionModel.rejectionReason = '';
          this.rejectionModel.rejectionSubmitted = false;
        }
        this.cancelLeadsRejection();
      }
    },


    // open confirmation action modal
    async openConfirmationActionModal(target, csvData, importType) {
      this.confirmCsvData = csvData;
      this.confirmImportType = importType;

      // set the Confirm message based on the target
      if (target === 'allLeads') {
        this.confirmMessage = this.$t('frontend_groovemail_admin_import_user_approval_are_you_sure_approve_leads_import');
      } else if (target === 'goodLead') {
        this.confirmMessage = this.$t('frontend_groovemail_admin_import_user_approval_are_you_sure_approve_good_leads_import');
      }

      // open the modal
      this.$refs[`confirmation-action`].open();
    },

    // close confirmation action modal
    closeConfirmationActionModal() {
      // reset the Confirm data
      this.confirmMessage = '';
      this.confirmCsvData = '';
      this.confirmImportType = '';
    },


    // confirm approval of all contacts
    async confirmAction() {
      try {
        const approvalResponse = await postData(
          adminUrls.importManagementUrl.approveGoodLeads,
          { id: this.confirmCsvData.id }
        );
        this.reloadImportDataAfterApproval(approvalResponse, this.confirmImportType);
      } catch (error) {
        // show error alert
        await showRequestErrorAlert(error?.response?.data?.error);
      }
    },


	  /**
     * Reloads data of imported contacts after approval of contacts
     * @param {object} approvalResponse approval response data
     * @param {number} importType contacts under different import status
     *    0 - pending imported contacts
     *    3 - rejected imported contacts
     */
    async reloadImportDataAfterApproval(approvalResponse, importType) {
	    if (importType === 0) {	
        if (	
          approvalResponse &&	
          approvalResponse.data &&	
          approvalResponse.data.status === 200 &&	
          this.$refs.pendingImportsListing &&	
          this.$refs.pendingImportsListing.$refs &&	
          this.$refs.pendingImportsListing.$refs.importListing &&	
          this.$refs.pendingImportsListing.$refs.importListing.$refs &&	
          this.$refs.pendingImportsListing.$refs.importListing.$refs.serverTable &&	
          this.$refs.pendingImportsListing.$refs.importListing.$refs.serverTable.$refs.importTable	
        ) {	
          const pageCount = this.getPageCount(	
            this.$refs.pendingImportsListing.$refs.importListing.$refs.serverTable.$refs.importTable.count,	
            this.$refs.pendingImportsListing.importedUsersTableOptions.perPage,	
            this.$refs.pendingImportsListing.$refs.importListing.$refs.serverTable.$refs.importTable.Page	
          );	
          await this.$refs.pendingImportsListing.$refs.importListing.$refs.serverTable.$refs.importTable.setPage(	pageCount);	
        }	
      } else if (
        importType === 3 &&
        approvalResponse &&	
        approvalResponse.data &&	
        approvalResponse.data.status === 200 &&	
        this.$refs.rejectedImportsListing &&	
        this.$refs.rejectedImportsListing.$refs &&	
        this.$refs.rejectedImportsListing.$refs.importListing &&	
        this.$refs.rejectedImportsListing.$refs.importListing.$refs &&	
        this.$refs.rejectedImportsListing.$refs.importListing.$refs.serverTable &&	
        this.$refs.rejectedImportsListing.$refs.importListing.$refs.serverTable.$refs.importTable	
      ) {	
        const pageCount = this.getPageCount(	
          this.$refs.rejectedImportsListing.$refs.importListing.$refs.serverTable.$refs.importTable.count,	
          this.$refs.rejectedImportsListing.importedUsersTableOptions.perPage,	
          this.$refs.rejectedImportsListing.$refs.importListing.$refs.serverTable.$refs.importTable.Page	
        );	
        await this.$refs.rejectedImportsListing.$refs.importListing.$refs.serverTable.$refs.importTable.setPage(	pageCount);	
      }
    },

    /**
     * Returns current page after execution of action based on certain parameters
     * @param {number} totalCount total count of data
     * @param {number} perPage per page number
     * @param {number} currentPage current page number
     * @return {number} current page
     */
    getPageCount(totalCount, perPage, currentPage) {
      let currPage = currentPage;
      if (totalCount !== 1 && totalCount % perPage === 1) {
        currPage = currentPage - 1;
      }
      return currPage;
    },

	  /**
     * Triggers opening of import responses modal after assigning data.
     * @param {object} csvData data related to csv file
     */
    showResponsesForThisCsv(csvData) {
      if (
        csvData.questions &&
        csvData.questions.length &&
        csvData.website_urls &&
        csvData.website_urls.length
      ) {
        this.importResponsesData = csvData;
      }
      this.$root.$emit('modal-open', 'import-responses-modal');
    },

	  /**
     * Triggers opening of import mapping modal after assigning data.
     * @param {object} csvData data related to csv file
     */
    showMappingsForThisCsv(csvData) {
      this.importData = csvData;
      this.$root.$emit('modal-open', 'import-mappings-modal');
    },

	  /**
     * Triggers opening of senders detail modal after assigning data.
     * @param {object} csvData data related to csv file
     */
    showGatewaysForThisCsv(csvData) {
      if (csvData.senders && csvData.senders.length) {
        this.sendersGatewayData = csvData.senders;
        this.$root.$emit('modal-open', 'senders-gateways-modal');
      }
    },

	  /**
     * Triggers change in table based on tab change.
     * @param {string} newTab new tab
     */
    handleTabChange(newTab) {
      this.currentTab = newTab;
      this.$router.push({ name: 'ImportUserApprovals' }).catch(() => {});
    },

	  /**
     * Triggers opening of new page in different tab for showing imports of specific user.
     * @param {string} type type of imported contacts
     * @param {string} email email of contact who imported
     */
    showImportsForThisUser(type, email) {
      const routeData = this.$router.resolve({
        name: 'ImportUserApprovals',
        query: { email, type },
      });
      window.open(routeData.href, '_blank');
    },
  },
};
</script>


<style lang="scss">
.gm-import-view-switch {
  flex: 0 0 auto;
  min-width: 220px;
}
</style>
